import { ColorEnum } from '@/constants/color'
import { ShiftCloseoutReconciliation } from '@/features/shiftCloseouts/types'
import CheckmarkIcon from '@/features/ui/icons/components/CheckmarkIcon'
import ExclamationCircleIcon from '@/features/ui/icons/components/ExclamationCircleIcon'
import Typography from '@/features/ui/typography'
import { formatPrice } from '@/utils/priceHelper'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface ReconciliationStatusProps {
    reconciliation: ShiftCloseoutReconciliation
}

const ReconciliationStatus: FC<ReconciliationStatusProps> = ({ reconciliation }) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, { state: reconciliation.state })

    const signedDifference = formatPrice(
        reconciliation.totalDifference.amount,
        reconciliation.totalDifference.currency,
        reconciliation.state === 'surplus'
    )

    const label = {
        balanced: t('shiftCloseouts.reconciliation.balanced'),
        incomplete: t('shiftCloseouts.reconciliation.incomplete'),
        mismatch: t('shiftCloseouts.reconciliation.mismatch'),
        shortage: t('shiftCloseouts.reconciliation.shortage', {
            amount: signedDifference,
        }),
        surplus: t('shiftCloseouts.reconciliation.surplus', {
            amount: signedDifference,
        }),
    }[reconciliation.state]
    const iconColor =
        reconciliation.state === 'surplus' ? ColorEnum.YELLOW_600 : ColorEnum.BRICK_500

    return (
        <View
            accessibilityLabel={label}
            accessibilityLiveRegion="polite"
            accessible
            style={styles.container}
            testID="shift-closeout-reconciliation-status"
        >
            <View style={styles.content}>
                {reconciliation.state === 'balanced' && (
                    <CheckmarkIcon fill={ColorEnum.Emerald600} height={16} width={16} />
                )}
                {['mismatch', 'shortage', 'surplus'].includes(reconciliation.state) && (
                    <ExclamationCircleIcon color={iconColor} height={16} width={16} />
                )}
                <Typography.Description font="bold" size="large" style={styles.text}>
                    {label}
                </Typography.Description>
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        minHeight: 48,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
        paddingVertical: 12,
        borderRadius: 8,
        variants: {
            state: {
                balanced: { backgroundColor: ColorEnum.EMERALD_100 },
                incomplete: { backgroundColor: ColorEnum.VIOLET_100 },
                mismatch: { backgroundColor: ColorEnum.Brick100 },
                shortage: { backgroundColor: ColorEnum.Brick100 },
                surplus: { backgroundColor: ColorEnum.Yellow100 },
            },
        },
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    text: {
        textAlign: 'center',
        variants: {
            state: {
                balanced: { color: ColorEnum.Emerald600 },
                incomplete: { color: ColorEnum.GRAY_600 },
                mismatch: { color: ColorEnum.BRICK_500 },
                shortage: { color: ColorEnum.BRICK_500 },
                surplus: { color: ColorEnum.YELLOW_600 },
            },
        },
    },
})

export default ReconciliationStatus
